import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { DataSource, DataSourceItem, DefaultDataSourceContext } from "@cervval.js/common/src/forms/dataSource";
import { ItemPropertyOrFunction, ValueOrFunction, getItemPOFValue, getVOFValue } from "@cervval.js/common/src/tools/object.tools";
import { ObjectTools } from "@cervval.js/common/tools";
import { ACTION_CACHE_SERVICE, ActionCacheService } from "../services/cache.service";

/**
 * Default data source implementation for a dtoCacheRepository, using the dto id (more precisely using {@link DTOCacheRepositoryOptions#getId}) as value
 */
export abstract class AbstractDtoDatasource<ElementTypes, TypeName extends keyof ElementTypes, V = string | number | null, DataSourceContext = DefaultDataSourceContext<ElementTypes[TypeName]>> implements DataSource<ElementTypes[TypeName], V, DataSourceContext> {

    @Injected(ACTION_CACHE_SERVICE)
    protected readonly _cache!: ActionCacheService<ElementTypes>;

    /** Type name of dtos */
    protected _typeName: TypeName;

    /** Label value of item in DataSourceItem */
    protected _labelPOF: ItemPropertyOrFunction<ElementTypes[TypeName], string>;

    /** 
     * Store label of datasource
     * This label is used when a list of datasources is displayed to users (ex: in selects/lists)
     * In theses cases, label is displayed to user instead of datasource uid
     * 
     * * This label isn't displayed in forms who are using the datasource
     */
    public _label?: ValueOrFunction<string>;

    /** Setter for label of datasource */
    public set label(value: ValueOrFunction<string>) {
        this._label = value;
    }

    /** Getter for displayable label of datasource */
    public get label(): string | undefined { return getVOFValue(this._label); }

    /** Collator use to sort items in @see GetItems */
    private _collator: Intl.Collator;

    /**
     * Constructs a data source for a list of objects
     * @param typeName type of dtos, the dtoCacheRepository for this type will be used to fetch the data
     * @param labelPOF will be used to get the displayString for the item.
     * @param filterItems optional filter function for getItems
     * @param collatorOptions optional options for collator used to sort in getItems
     */
    constructor(
        typeName: TypeName,
        labelPOF: ItemPropertyOrFunction<ElementTypes[TypeName], string>,
        public filterItems?: (dto: ElementTypes[TypeName], context?: DataSourceContext) => boolean,
        options?: {
            datasourceLabel?: ValueOrFunction<string>
        },
        collatorOptions?: Intl.CollatorOptions
    ) {
        this._typeName = typeName;
        this._labelPOF = labelPOF;
        this.label = options?.datasourceLabel;
        this._collator = new Intl.Collator(undefined, {
            sensitivity: "base",
            usage: "sort",
            ...collatorOptions
        });
    }

    /** Get a string from value */
    public getDisplayString(value: V, itemFromCache?: DataSourceItem<ElementTypes[TypeName]>): string {
        const dataSourceItem = itemFromCache ?? this.getItemFromValue(value);
        return getItemPOFValue(dataSourceItem?.item, this._labelPOF) ?? "";
    }

    /** 
     * Get list of DataSource items from context
     * Items sort by display string ascendant order
     */
    public getItems(context?: DataSourceContext): DataSourceItem<ElementTypes[TypeName]>[] {
        const dtoList = this._cache.getDTOCacheRepository(this._typeName).dtoList;
        const itemList: DataSourceItem<ElementTypes[TypeName]>[] = [];
        for (const dto of dtoList) {
            if (this.filterItems == null || this.filterItems(dto, context)) {
                itemList.push({ item: dto });
            }
        }
        itemList.sort((a, b) => {
            const displayStringA = getItemPOFValue(a.item, this._labelPOF);
            const displayStringB = getItemPOFValue(b.item, this._labelPOF);

            return ObjectTools.localeCompare(displayStringA, displayStringB, this._collator);
        });
        return itemList;

    }

    /** @override */
    public getObjects(context?: DataSourceContext): ElementTypes[TypeName][] {

        const dtoList = this._cache.getDTOCacheRepository(this._typeName).dtoList;
        const objects: ElementTypes[TypeName][] = [];
        for (const object of dtoList) {
            if (this.filterItems == null || this.filterItems(object, context)) {
                if (object == null) { continue; }
                objects.push(object);
            }
        }
        return objects;
    }

    /** @override */
    public abstract getValueFromItem(dataSourceItem: DataSourceItem<ElementTypes[TypeName]>): V | undefined;

    /** Get a DataSource item from value */
    public abstract getItemFromValue(value: V): DataSourceItem<ElementTypes[TypeName]> | undefined;

}